import React, { useEffect, useState } from 'react';

import { Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { useMutation, useQuery, queryCache } from 'react-query';
import { DotBricksLoading, ListPagination, Toast } from 'src/common/components';
import { useDebouncedValue } from 'src/common/hooks';

import { fetchListings, setOfficeVisibility, setProviderVisibility } from '../api';
import { Listing, ListingProvider, Paginated } from '../types';

// Labelled around the `show_listing` staff switch specifically, not "is this
// office actually visible to patients" — cosmos's `visibility` filter param
// only looks at `show_listing` (CitasListingAdminFilterSet.filter_visibility),
// so an office that's on here can still be effectively hidden for not
// accepting new patients. `officeVisibilityStatus` below is the source of
// truth for that; these labels just avoid implying this filter is it too.
const VISIBILITY_FILTERS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'visible', label: 'Listing on' },
  { key: 'hidden', label: 'Listing off' },
];

const Switch: React.FC<{
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  describedBy?: string;
}> = ({ on, disabled = false, onClick, label, describedBy }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    aria-describedby={describedBy}
    aria-busy={disabled}
    disabled={disabled}
    onClick={onClick}
    className={`relative inline-flex flex-shrink-0 items-center h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-2.0 disabled:opacity-50 ${
      on ? 'bg-green-500' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
        on ? 'translate-x-4' : 'translate-x-1'
      }`}
    />
  </button>
);

const ProviderDots: React.FC<{ providers: ListingProvider[] }> = ({ providers }) => {
  const shown = providers.slice(0, 8);
  const extra = providers.length - shown.length;
  const active = providers.filter((p) => p.is_active).length;
  return (
    <span className="flex items-center gap-1.5">
      <span className="flex items-center gap-1">
        {shown.map((p) => (
          <span
            key={p.id}
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              p.is_active ? 'bg-green-500' : 'bg-gray-300'
            }`}
          />
        ))}
        {extra > 0 && <span className="text-gray-400">+{extra}</span>}
      </span>
      <span className="text-gray-500">
        {providers.length === 1 ? 'provider' : 'providers'} {active}/{providers.length} on
      </span>
    </span>
  );
};

const ProviderRow: React.FC<{ accountId: string; provider: ListingProvider }> = ({
  accountId,
  provider,
}) => {
  const [toggle, { isLoading }] = useMutation(
    (isActive: boolean) => setProviderVisibility(accountId, provider.id, isActive),
    {
      onSuccess: (_res, isActive) => {
        Toast.success(`${provider.name} ${isActive ? 'shown' : 'hidden'}.`);
        queryCache.invalidateQueries('listings');
      },
      onError: () => {
        Toast.error('Could not update the provider.');
      },
    }
  );

  const handleToggle = (): void => {
    if (provider.is_active) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Hide ${provider.name} from the public directory?`)) return;
      toggle(false);
    } else {
      toggle(true);
    }
  };

  return (
    <div className="hover:bg-gray-50 flex items-center justify-between py-2 pl-16 pr-4 border-t border-gray-100">
      <div className="flex items-center min-w-0 gap-2 text-sm">
        <span className={`truncate ${provider.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
          {provider.name}
        </span>
        {provider.specialty && (
          <span className="flex-shrink-0 text-xs text-gray-400">· {provider.specialty}</span>
        )}
      </div>
      <div className="flex items-center flex-shrink-0 gap-2">
        <span className={`text-xs ${provider.is_active ? 'text-green-700' : 'text-gray-400'}`}>
          {provider.is_active ? 'Visible' : 'Hidden'}
        </span>
        <Switch
          on={provider.is_active}
          disabled={isLoading}
          onClick={handleToggle}
          label={provider.is_active ? `Hide ${provider.name}` : `Show ${provider.name}`}
        />
      </div>
    </div>
  );
};

// `show_listing` is the staff switch; `accepts_new_patients` is the office's
// own setting, filtered by cosmos into the same public queryset. An office can
// end up hidden by either one, and the two need visibly different reasons —
// otherwise "no acepta pacientes nuevos" reads as a staff action nobody took.
const officeVisibilityStatus = (
  office: Listing
): { hidden: boolean; label: string; accent: string; text: string; providersNote?: string } => {
  if (!office.show_listing) {
    return {
      hidden: true,
      label: 'Hidden',
      accent: 'bg-gray-300',
      text: 'text-gray-400',
      providersNote:
        'Office is hidden — providers are not shown publicly regardless of their toggle.',
    };
  }
  // Strictly `=== false`, not `!office.accepts_new_patients`: the field is
  // absent on a cosmos version predating this rollout (or a stale cached
  // response), and failing closed on a display-only reason would badge every
  // visible office as hidden instead of just not showing the reason yet.
  if (office.accepts_new_patients === false) {
    return {
      hidden: true,
      label: 'Hidden — no acepta pacientes nuevos',
      accent: 'bg-amber-400',
      text: 'text-amber-700',
      providersNote:
        'Office does not accept new patients — providers are not shown publicly regardless of their toggle.',
    };
  }
  return { hidden: false, label: 'Visible', accent: 'bg-green-500', text: 'text-green-700' };
};

const OfficeCard: React.FC<{ office: Listing }> = ({ office }) => {
  const [expanded, setExpanded] = useState(false);
  const hasProviders = office.providers.length > 0;
  const visibility = officeVisibilityStatus(office);

  const [toggle, { isLoading }] = useMutation(
    (show: boolean) => setOfficeVisibility(office.account_id, show),
    {
      onSuccess: (_res, show) => {
        Toast.success(`${office.name} ${show ? 'shown' : 'hidden'}.`);
        queryCache.invalidateQueries('listings');
      },
      onError: () => {
        Toast.error('Could not update the office.');
      },
    }
  );

  const handleToggle = (): void => {
    if (office.show_listing) {
      // eslint-disable-next-line no-alert
      if (
        !window.confirm(
          `Hide ${office.name} from the public directory? This also hides all its providers.`
        )
      )
        return;
      toggle(false);
    } else {
      toggle(true);
    }
  };

  return (
    <div className="mb-2.5 flex overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className={`w-1 flex-shrink-0 ${visibility.accent}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => hasProviders && setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${office.name}`}
            className="flex items-center flex-1 min-w-0 gap-3 text-left"
          >
            {hasProviders ? (
              expanded ? (
                <ChevronDown size={16} className="flex-shrink-0 text-gray-400" />
              ) : (
                <ChevronRight size={16} className="flex-shrink-0 text-gray-400" />
              )
            ) : (
              <span className="flex-shrink-0 w-4" />
            )}
            <span className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
              <Building2 size={16} className="text-gray-500" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-gray-900 truncate">
                {office.name}
              </span>
              <span className="flex items-center gap-2 text-xs text-gray-400">
                {office.city && <span className="flex-shrink-0">{office.city}</span>}
                {office.city && <span className="text-gray-300">•</span>}
                {hasProviders ? (
                  <ProviderDots providers={office.providers} />
                ) : (
                  <span>No providers</span>
                )}
              </span>
            </span>
          </button>
          <div className="flex items-center flex-shrink-0 gap-2">
            <span
              id={`visibility-${office.account_id}`}
              className={`text-xs font-medium ${visibility.text}`}
            >
              {visibility.label}
            </span>
            <Switch
              on={office.show_listing}
              disabled={isLoading}
              onClick={handleToggle}
              label={office.show_listing ? `Hide ${office.name}` : `Show ${office.name}`}
              describedBy={`visibility-${office.account_id}`}
            />
          </div>
        </div>

        {expanded && hasProviders && (
          <div className={visibility.hidden ? 'opacity-60' : ''}>
            {visibility.providersNote && (
              <p className="pt-2 pb-1 pl-16 pr-4 text-xs text-gray-400">
                {visibility.providersNote}
              </p>
            )}
            {office.providers.map((provider) => (
              <ProviderRow key={provider.id} accountId={office.account_id} provider={provider} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const Listings: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [visibility, setVisibility] = useState('');
  const search = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [search]);

  const { data, isLoading, isError } = useQuery<{ data: Paginated<Listing> }>(
    ['listings', page, search, visibility],
    () => fetchListings({ page, search, visibility }),
    { keepPreviousData: true }
  );

  const listings = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full pt-8 pb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Listings</h2>
        <input
          type="search"
          placeholder="Search office or city…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-2.0 w-64"
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        {VISIBILITY_FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            type="button"
            onClick={() => {
              setVisibility(f.key);
              setPage(1);
            }}
            className={`px-3 py-1 rounded text-sm border transition-colors ${
              visibility === f.key
                ? 'bg-primary-2.0 text-white border-primary-2.0'
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <DotBricksLoading />
      ) : isError ? (
        <p className="text-red-600">Failed to load listings. Please try again.</p>
      ) : listings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-base font-medium">No listings found</p>
          <p className="mt-1 text-sm">Try a different search or filter.</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-auto">
            {listings.map((office) => (
              <OfficeCard key={office.account_id} office={office} />
            ))}
          </div>
          <ListPagination totalCount={totalCount} currentPage={page} setCurrentPage={setPage} />
        </div>
      )}
    </div>
  );
};
